import React, { useState } from 'react';
import { toast } from 'sonner';

type Shortcode = {
  shortcode: string;
  description: string;
  requiresCustomLanguage?: boolean;
};

const SingleShortcodeDocs: React.FC = () => {
  const shortcodes: Shortcode[] = [
    { shortcode: '[Fp_Single_Fajr_Begins_time]', description: 'Displays the Fajr prayer begins time (e.g., "05:10 AM").' },
    { shortcode: '[Fp_Single_Fajr_Iqamah_time]', description: 'Displays the Fajr prayer iqamah time (e.g., "05:17 AM").' },
    { shortcode: '[Fp_Single_Dhuhr_Begins_time]', description: 'Displays the Dhuhr prayer begins time (e.g., "12:30 PM").' },
    { shortcode: '[Fp_Single_Dhuhr_Iqamah_time]', description: 'Displays the Dhuhr prayer iqamah time (e.g., "12:37 PM").' },
    { shortcode: '[Fp_Single_Asr_Begins_time]', description: 'Displays the Asr prayer begins time (e.g., "03:45 PM").' },
    { shortcode: '[Fp_Single_Asr_Iqamah_time]', description: 'Displays the Asr prayer iqamah time (e.g., "03:52 PM").' },
    { shortcode: '[Fp_Single_Maghrib_Begins_time]', description: 'Displays the Maghrib prayer begins time (e.g., "06:10 PM").' },
    { shortcode: '[Fp_Single_Maghrib_Iqamah_time]', description: 'Displays the Maghrib prayer iqamah time (e.g., "06:17 PM").' },
    { shortcode: '[Fp_Single_Isha_Begins_time]', description: 'Displays the Isha prayer begins time (e.g., "07:45 PM").' },
    { shortcode: '[Fp_Single_Isha_Iqamah_time]', description: 'Displays the Isha prayer iqamah time (e.g., "07:52 PM").' },
    { shortcode: '[Fp_Single_Midnight_Begins_time]', description: 'Displays the midnight time also known as Qiyām Al-Layl (e.g., "11:50 PM").' },
    { 
      shortcode: '[Fp_Single_Midnight_Name]', 
      description: 'Displays the midnight name also known as Qiyām Al-Layl (e.g., "Midnight").',
      requiresCustomLanguage: true 
    },
    { shortcode: '[Fp_Single_Sunrise_Begins_time]', description: 'Displays the sunrise time (e.g., "05:10 AM").' },
  
    { 
      shortcode: '[Fp_Single_Prayer_Begins_Title]', 
      description: 'Displays the begins title (e.g., "Begins").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Prayer_Iqamah_Title]', 
      description: 'Displays the iqamah title (e.g., "Iqamah").',
      requiresCustomLanguage: true 
    },
  
    { 
      shortcode: '[Fp_Single_Fajr_Name]', 
      description: 'Displays the name of Fajr prayer (e.g., "Fajr").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Dhuhr_Name]', 
      description: 'Displays the name of Dhuhr prayer (e.g., "Dhuhr").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Asr_Name]', 
      description: 'Displays the name of Asr prayer (e.g., "Asr").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Maghrib_Name]', 
      description: 'Displays the name of Maghrib prayer (e.g., "Maghrib").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Isha_Name]', 
      description: 'Displays the name of Isha prayer (e.g., "Isha").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Sunrise_Name]', 
      description: 'Displays the name of Sunrise (e.g., "Sunrise").',
      requiresCustomLanguage: true 
    },
  
    { shortcode: '[Fp_Single_CurrentDate]', description: 'Displays the current Gregorian date (e.g., "20 April 2025").' },
    { shortcode: '[Fp_Single_Hijra_Date]', description: 'Displays the current Hijri date (e.g., "15 Ramadan 1447").' },
    { shortcode: '[Fp_Single_CurrentTime]', description: 'Displays the current time with live updates (e.g., "05:10 AM").' },
    { 
      shortcode: '[Fp_Single_PTFT]', 
      description: 'Displays the text set in the Translate settings (e.g., "Prayer Times For Today").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Current_Title]', 
      description: 'Displays the current prayer title set in the Translate settings (e.g., "The Current Prayer").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Prayer_Title]', 
      description: 'Displays the static text "Prayer" from Translate settings, used in Vertical Daily Prayer header.',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Current_Location]', 
      description: 'Displays the location text set in the Translate settings (e.g., "Current Location").',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Masjid_Name]', 
      description: 'Displays the masjid name set in the Translate settings (e.g., "Masjid Al-Noor").',
      requiresCustomLanguage: true 
    },
  
    { 
      shortcode: '[Fp_Single_Next_Prayer_Iqamah_Time]', 
      description: 'Displays the iqamah time of the next prayer (e.g., "05:00 AM"). Dynamically updates based on the next upcoming prayer.' 
    },
    { 
      shortcode: '[Fp_Single_Next_Prayer_Iqamah_Title]', 
      description: 'Displays the iqamah title of the next prayer (e.g., "Fajr Iqamah"). Dynamically updates based on the next upcoming prayer.',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Next_Prayer_Begins_Time]', 
      description: 'Displays the begins time of the next prayer (e.g., "05:10 AM"). Dynamically updates based on the next upcoming prayer.' 
    },
    { 
      shortcode: '[Fp_Single_Next_Prayer_Begins_Title]', 
      description: 'Displays the next prayer title dynamically (e.g., "Fajr Begins", "Dhuhr Begins").',
      requiresCustomLanguage: true 
    },
    {
      shortcode: '[Fp_Single_Next_Prayer_Begins_Title removeNextWord="true"]',
      description: 'Displays the next prayer title dynamically (e.g., "Fajr"). Removes the word "Begins" from the output.',
      requiresCustomLanguage: true 
    },
    {
      shortcode: '[Fp_Single_Next_Prayer_Begins_Title removeSpecificWord="Fajr or Begins or any words you want"]',
      description: 'Displays the next prayer title dynamically. You can remove any specific word (e.g., "Fajr", "Begins", "Iqamah") from the output using this attribute.',
      requiresCustomLanguage: true 
    },
    {
      shortcode: '[Fp_Single_Next_Prayer_Begins_Title remove_next_word="true"]',
      description: 'Same as removeNextWord but using snake_case format. Removes the word "Begins" from the output.',
      requiresCustomLanguage: true 
    },
    {
      shortcode: '[Fp_Single_Next_Prayer_Begins_Title remove_specific_word="Fajr or Begins or any words you want"]',
      description: 'Same as removeSpecificWord but using snake_case. Removes a specified word (e.g., "Begins", "Fajr") from the dynamically changing next prayer title.',
      requiresCustomLanguage: true 
    },
    { 
      shortcode: '[Fp_Single_Next_Prayer_Title]', 
      description: 'Displays the static text "Next Prayer".',
      requiresCustomLanguage: true 
    }
  ];

  const [copiedShortcodes, setCopiedShortcodes] = useState<{ [key: string]: boolean }>({});

  const copyToClipboard = (shortcode: string) => {
    if (window.location.protocol !== 'https:') {
      toast.warning(
        "Copy function doesn't work on HTTP. Please switch to HTTPS or use right-click to copy manually."
      );
      return;
    }

    navigator.clipboard
      .writeText(shortcode)
      .then(() => {
        setCopiedShortcodes((prev) => ({ ...prev, [shortcode]: true }));
        toast.success('Shortcode copied to clipboard!');

        setTimeout(() => {
          setCopiedShortcodes((prev) => ({ ...prev, [shortcode]: false }));
        }, 2000);
      })
      .catch((err) => {
        console.error('Clipboard API failed:', err);
        toast.error('Failed to copy shortcode.');
      });
  };

  return (
    <div className="font-sans p-5">
      <h1 className="text-2xl font-bold mb-4">FivePrayer Shortcodes Documentation</h1>
      
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <h2 className="font-bold text-lg mb-2 text-blue-800">About Translation Shortcodes</h2>
        <p className="text-blue-700">
          Shortcodes marked with <span className="bg-blue-100 px-2 py-1 rounded text-sm font-mono">Customizable Text</span> will display in English by default.
          To change these texts to another language or customize the default English text, follow these steps:
        </p>
        <ol className="list-decimal list-inside mt-2 text-blue-700 space-y-1">
          <li>Go to <span className="font-medium">FivePrayer → Hijri Calendar</span></li>
          <li>Enable <span className="font-medium">Active Hijri Calendar</span> option</li>
          <li>Select your preferred <span className="font-medium">Calendar Type</span> (Umm al-Qura, etc.)</li>
          <li>Enable the <span className="font-medium">Custom Language</span> option in the same section</li>
        </ol>
      </div>

      <p className="mb-6 text-gray-700">
        Below is a list of all available shortcodes for displaying prayer times and
        other related information. Click the "Copy" button to copy the shortcode to
        your clipboard.
      </p>

      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-3 border border-gray-300">Shortcode</th>
            <th className="p-3 border border-gray-300">Description</th>
            <th className="p-3 border border-gray-300">Action</th>
          </tr>
        </thead>
        <tbody>
          {shortcodes.map((item, index) => (
            <tr key={index} className="border-b border-gray-300">
              <td className="p-3 border border-gray-300">
                <code className="bg-gray-100 p-1 rounded">{item.shortcode}</code>
                {item.requiresCustomLanguage && (
                  <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    Customizable Text
                  </span>
                )}
              </td>
              <td className="p-3 border border-gray-300">{item.description}</td>
              <td className="p-3 border border-gray-300">
                <button
                  onClick={() => copyToClipboard(item.shortcode)}
                  className={`px-4 py-2 rounded w-24 ${
                    copiedShortcodes[item.shortcode]
                      ? 'bg-green-500 text-white'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  } transition-colors`}
                >
                  {copiedShortcodes[item.shortcode] ? 'Copied!' : 'Copy'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SingleShortcodeDocs;